import { getBaseRole } from '@/helpers/customRoles';
import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import usePagination from '@/hooks/usePagination'
import Pagination from '@UI/New/Pagination'
import { directionOrdersApi, reportAPI } from '@/api'
import { IFinancialTransactionsReportData } from './utils/types'
import ExerciseHomeworkReportTable from './FinancialTransactionsReportTable/FinancialTransactionsReportTable'
import cn from './index.module.css'
import { FinancialTransactionsReportFilters } from './FinancialTransactionsReportFilters'
import Button from '@UI/New/Button'
import { IcoDownload } from '@/utils/icons'
import { useDownloadFile } from '@/hooks/useDownloadFile'
import { Modal, Switch } from 'antd'
import { SettingOutlined } from '@ant-design/icons'
import { useSelector } from 'react-redux'
import { selectProfile } from '@/store/profile.reducer'
import { useAddAlert } from '@/hooks/useAddAlert'
import {
  HIDE_ZERO_PAYMENTS_SETTING_KEY,
  shouldHideZeroPaymentsByDefault,
} from '@/helpers/financialTransactions'
import RoleBasedTableSettingsModal from '@common/CustomTable/RoleBasedTableSettings/RoleBasedTableSettingsModal'
import {
  FINANCIAL_TRANSACTIONS_ACCEPTED_ROLES,
  FINANCIAL_TRANSACTIONS_COLUMNS,
  FINANCIAL_TRANSACTIONS_EXCLUDED_COLUMNS_BY_ROLE,
  FINANCIAL_TRANSACTIONS_TABLE_SETTING_KEY,
} from './FinancialTransactionsReportTable/tableSettings'

interface IFinancialTransactionsReport {
  userId: number
  filters?: IFilters
  setFilters?: Dispatch<SetStateAction<IFilters>>
  showDownload?: boolean
  actionsContainer?: HTMLElement | null
}

export interface IFilters {
  directionId: number | null | string;
  balanceId: string | null;
  status: string | null;
  hideZeroAmountPayments?: boolean;
}

const initFilters: IFilters = {
  directionId: null,
  balanceId: null,
  status: null,
};

const FinancialTransactionsReport: FC<IFinancialTransactionsReport> = ({
  userId,
  filters: controlledFilters,
  setFilters: setControlledFilters,
  showDownload = true,
  actionsContainer,
}) => {
  const profile = useSelector(selectProfile)
  const hideZeroPaymentsByDefault = useSelector((store: any) =>
    shouldHideZeroPaymentsByDefault(
      store.settings?.[HIDE_ZERO_PAYMENTS_SETTING_KEY],
    ),
  )
  const addAlert = useAddAlert()
  const { download, loading: downloadLoading } = useDownloadFile()
  const [sorting, setSorting] = useState<
    { key: string; order: 'asc' | 'desc' | 'none' }[]
  >(
    [].map((item) => {
      return { key: item, order: 'none' }
    }),
  )
  const [tableSettingsOpen, setTableSettingsOpen] = useState(false)
  
  const [localFilters, setLocalFilters] = useState<IFilters>(() => ({
    ...initFilters,
    hideZeroAmountPayments: hideZeroPaymentsByDefault,
  }));
  const filters = controlledFilters ?? localFilters
  const setFilters = setControlledFilters ?? setLocalFilters
  const hideZeroAmountPayments =
    filters.hideZeroAmountPayments ?? hideZeroPaymentsByDefault

  const onSortingClick = (key: string) => {
    setSorting((prev) =>
      prev.map((item) => {
        const order =
          key === item.key
            ? item.order === 'none'
              ? 'asc'
              : item.order === 'asc'
              ? 'desc'
              : 'none'
            : 'none'

        return { ...item, order: order }
      }),
    )
  }

  const getFilters = () => {
    const filterParams: Record<string, string> = {}

    filterParams['user-id'] = String(userId)
    if (filters.balanceId || filters.directionId) {
      filterParams['direction_id'] = String(filters.balanceId || filters.directionId)
    }
    if (filters.status) filterParams.status = filters.status
    if (hideZeroAmountPayments) filterParams['hide-zero-amount'] = '1'

    return filterParams
  }

  const handleDownloadExcel = () => {
    download({
      request: () =>
        directionOrdersApi.getHistoryExcel({
          id: userId,
          ...(filters.balanceId || filters.directionId
            ? { direction_id: filters.balanceId || filters.directionId }
            : {}),
          ...(filters.status ? { status: filters.status } : {}),
          ...(hideZeroAmountPayments ? { 'hide-zero-amount': 1 } : {}),
        }),
      name: 'История списаний и начислений',
      alertText: 'Файл скачан',
    })
  }

  const {
    state,
    loading,
    getState: getData,
    setState,
    ...params
  } = usePagination({
    api: directionOrdersApi.getHistory,
    latestRequestOnly: true,
    params: { ...getFilters() },
  })

  useEffect(() => {
    const handler = setTimeout(() => {
      getData({ page: 1 })
      params.setCurrentPage(1)
    }, 250)

    return () => {
      clearTimeout(handler)
    }
  }, [sorting, userId, filters])

  const reportData = state as unknown as IFinancialTransactionsReportData

  const handleRemove = (paymentRowId: number) => {
    Modal.confirm({
      title: 'Удалить строку?',
      content:
        'Строка будет удалена из отчёта. Если операция меняла баланс или срок доступа, изменение будет отменено.',
      okText: 'Удалить',
      cancelText: 'Отмена',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const response = await reportAPI.payments.paymentsRemove(paymentRowId)
          if (response?.data?.status !== 'success') throw new Error()
          addAlert({ title: 'Строка удалена' })
          await getData()
        } catch (error) {
          addAlert({ type: 'error', title: 'Не удалось удалить строку' })
        }
      },
    })
  }

  const tableSettingsButton = ['admin', 'junior-admin'].includes(profile?.role ?? '') ? (
    <Button
      color="gray"
      className={cn.actionButton}
      onClick={() => setTableSettingsOpen(true)}
      leftContent={
        <SettingOutlined
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        />
      }
    >
      Настройка таблицы
    </Button>
  ) : null

  return (
    <div className={cn.content}>
      <div className={cn.toolbar}>
        <FinancialTransactionsReportFilters {...{filters, setFilters, userId}} />
        <div className={cn.toolbarActions}>
          <label className={cn.zeroPaymentsSwitch}>
            <Switch
              size="small"
              checked={hideZeroAmountPayments}
              onChange={(value) =>
                setFilters((currentFilters) => ({
                  ...currentFilters,
                  hideZeroAmountPayments: value,
                }))
              }
              aria-label="Не отображать оплаты с нулевой суммой"
            />
            <span>Не отображать оплаты с нулевой суммой</span>
          </label>
          {actionsContainer
            ? createPortal(tableSettingsButton, actionsContainer)
            : tableSettingsButton}
          {showDownload && (
            <Button
              color="gray"
              className={cn.actionButton}
              onClick={handleDownloadExcel}
              disabled={downloadLoading}
              isLoading={downloadLoading}
              leftContent={<IcoDownload style={{ transform: 'rotate(180deg)' }} />}
            >
              Скачать Excel
            </Button>
          )}
        </div>
      </div>
      <ExerciseHomeworkReportTable
        loading={loading}
        data={reportData}
        sorting={sorting}
        onSortingClick={onSortingClick}
        canRemove={getBaseRole(profile?.role) === 'admin'}
        onRemove={handleRemove}
        hideZeroAmountPayments={hideZeroAmountPayments}
      />
      <Pagination compact className={cn.pagination} {...params} />
      <RoleBasedTableSettingsModal
        open={tableSettingsOpen}
        onDismiss={() => setTableSettingsOpen(false)}
        title="Настройка истории списаний и начислений"
        settingKey={FINANCIAL_TRANSACTIONS_TABLE_SETTING_KEY}
        columnsMap={FINANCIAL_TRANSACTIONS_COLUMNS}
        acceptedRoles={FINANCIAL_TRANSACTIONS_ACCEPTED_ROLES}
        excludedColumnsByRole={FINANCIAL_TRANSACTIONS_EXCLUDED_COLUMNS_BY_ROLE}
        rolesLabel="Для кого настраиваются столбцы"
        description="Для каждой роли можно отдельно включить столбцы и изменить их порядок"
      />
    </div>
  )
}

export default FinancialTransactionsReport
